/**
 * React hook for reading a child's photo from IndexedDB as an object URL.
 *
 * Handles the full lifecycle:
 *   1. Load the Blob on mount (or when `childId` changes)
 *   2. Create an object URL for rendering
 *   3. Revoke the URL on unmount or before replacing it
 *   4. Expose a `refresh()` callback for callers that just wrote a
 *      new photo and want the UI to pick it up without a page reload
 *
 * Returns `{ url, isLoading, refresh }`. `url` is `null` until the
 * load completes; it's also `null` if the child has no stored photo.
 * Components should render a default silhouette when `url === null`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { getChildPhotoBlob } from "./photos";

interface UsePhotoURLResult {
  url: string | null;
  isLoading: boolean;
  refresh: () => void;
}

export function usePhotoURL(childId: string | null | undefined): UsePhotoURLResult {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tick, setTick] = useState(0);

  // Track the current URL so we can revoke it on replacement.
  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Guard against races: if `childId` changes mid-fetch we should
    // ignore the stale result.
    let cancelled = false;

    if (childId === null || childId === undefined) {
      setUrl(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getChildPhotoBlob(childId)
      .then((blob) => {
        if (cancelled) return;
        // Revoke any previous URL before replacing.
        if (currentUrlRef.current !== null) {
          URL.revokeObjectURL(currentUrlRef.current);
          currentUrlRef.current = null;
        }
        if (blob === null) {
          setUrl(null);
        } else {
          const next = URL.createObjectURL(blob);
          currentUrlRef.current = next;
          setUrl(next);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setUrl(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [childId, tick]);

  // Final cleanup on unmount — revoke whatever URL is live.
  useEffect(() => {
    return () => {
      if (currentUrlRef.current !== null) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    };
  }, []);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  return { url, isLoading, refresh };
}
